if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.boundPreprocessHTML = this.preprocessHTML.bind(this);
        this.cleanupTimer = null;
        this.variantUiObserver = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.boundPreprocessHTML);
          this.scheduleVariantUiCleanup();
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.teardownObservers();

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.setOpenerLoading(opener, true);

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.initializePostRenderFeatures();
            super.show(opener);
          })
          .finally(() => {
            this.setOpenerLoading(opener, false);
          });
      }

      initializePostRenderFeatures() {
        this.rebuildCompactLayout();
        this.scheduleVariantUiCleanup();
        this.setupObservers();
      }

      setupObservers() {
        this.teardownObservers();
        if (!this.modalContent) return;

        this.variantUiObserver = new MutationObserver(() => this.scheduleVariantUiCleanup());
        this.variantUiObserver.observe(this.modalContent, {
          childList: true,
          subtree: true,
        });
      }

      teardownObservers() {
        if (this.variantUiObserver) {
          this.variantUiObserver.disconnect();
          this.variantUiObserver = null;
        }

        if (this.cleanupTimer) {
          window.clearTimeout(this.cleanupTimer);
          this.cleanupTimer = null;
        }
      }

      scheduleVariantUiCleanup() {
        if (this.cleanupTimer) window.clearTimeout(this.cleanupTimer);

        this.cleanupTimer = window.setTimeout(() => {
          this.normalizeVariantSelects();
          this.removeDuplicateVariantGroups();
        }, 80);
      }

      setOpenerLoading(opener, isLoading) {
        if (!opener) return;

        opener.toggleAttribute('aria-disabled', isLoading);
        opener.classList.toggle('loading', isLoading);
        opener.classList.toggle('sf-is-loading', isLoading);

        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const selectorsToRemove = [
          'pickup-availability',
          'product-modal',
          'modal-dialog',
          '.od-offerbar',
          '.od-quick-offers',
          '.od-bundle-offers',
          '.od-countdown',
          '.onhow-countdown',
          '.onhow-estimated-delivery',
          '.onhow-satisfaction-container',
          '.onhow-trust-badges',
          '#ModelInfo',
          '.product__model-info',
          '.product-form__quantity',
          '.quantity__rules',
          '.price-per-item__container',
          '.product__inventory',
          '.product__tax',
          '.product__sku',
          '.product__description',
          '.product__accordion',
          '.product-popup-modal__opener',
          '.share-button',
          '.icon-with-text',
          '.product__view-details',
          '.complementary-products',
          '.shopify-payment-button',
          'sticky-buy-bar',
          '[id^="shopify-block"][id*="judge_me"]',
          '.jdgm-widget',
          '.jdgm-rev-widg',
        ];

        productElement.querySelectorAll(selectorsToRemove.join(',')).forEach((node) => node.remove());

        // Keep quick-add focused and compact: only title, price, variants, and purchase actions.
        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) return;

        const keepSelectors = [
          '.product__title',
          '[id^="price-"]',
          '[id^="Price-"]',
          'variant-selects',
          '.product-form',
        ];

        Array.from(infoContainer.children).forEach((child) => {
          const shouldKeep = keepSelectors.some((selector) => child.matches(selector) || child.querySelector(selector));
          if (!shouldKeep) child.remove();
        });
      }

      getVariantGroupKey(group) {
        const rawLabel = group.querySelector('.form__label')?.textContent || '';
        return rawLabel.replace(/\s+/g, ' ').trim().toLowerCase().replace(/:.*/, '');
      }

      normalizeVariantSelects() {
        const allVariantSelects = Array.from(this.modalContent.querySelectorAll('variant-selects'));
        if (allVariantSelects.length <= 1) return;

        // `viewTransition` leaves old nodes for 500ms with inline display:none.
        allVariantSelects
          .filter((node) => node.style.display === 'none')
          .forEach((node) => node.remove());

        const visibleVariantSelects = Array.from(this.modalContent.querySelectorAll('variant-selects')).filter(
          (node) => node.style.display !== 'none'
        );

        if (visibleVariantSelects.length <= 1) return;

        // Keep the first visible block: new content is inserted before old content.
        const keepNode = visibleVariantSelects[0];
        visibleVariantSelects.forEach((node) => {
          if (node !== keepNode) node.remove();
        });
      }

      removeDuplicateVariantGroups() {
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects) return;

        const groups = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        if (groups.length <= 1) return;

        const seen = new Set();
        for (let index = groups.length - 1; index >= 0; index -= 1) {
          const group = groups[index];
          const key = this.getVariantGroupKey(group);
          if (!key) continue;

          if (seen.has(key)) {
            group.remove();
            continue;
          }

          seen.add(key);
        }
      }

      removeColorVariantWhenSizeExists() {
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects) return;

        const variantInputs = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        if (!variantInputs.length) return;

        const hasSize = variantInputs.some((group) => this.getVariantGroupKey(group).startsWith('size'));
        if (!hasSize) return;

        variantInputs.forEach((group) => {
          const key = this.getVariantGroupKey(group);
          if (key.startsWith('color') || key.startsWith('colour')) {
            group.remove();
          }
        });
      }

      rebuildCompactLayout() {
        const product = this.modalContent.querySelector('.product');
        const mediaWrapper = product?.querySelector('.product__media-wrapper');
        const infoWrapper = product?.querySelector('.product__info-wrapper');
        const infoContainer = infoWrapper?.querySelector('.product__info-container');
        if (!product || !mediaWrapper || !infoWrapper || !infoContainer) return;
        if (infoContainer.querySelector('.sf-quickadd-summary')) return;

        const title = infoContainer.querySelector('.product__title');
        const price = infoContainer.querySelector('[id^="price-"], [id^="Price-"]');

        const summary = document.createElement('div');
        summary.className = 'sf-quickadd-summary';

        const mediaCol = document.createElement('div');
        mediaCol.className = 'sf-quickadd-summary__media';
        const sourceImage = mediaWrapper.querySelector('img');
        if (sourceImage) {
          const thumbnail = document.createElement('img');
          thumbnail.src = sourceImage.currentSrc || sourceImage.src || '';
          if (sourceImage.srcset) thumbnail.srcset = sourceImage.srcset;
          if (sourceImage.sizes) thumbnail.sizes = sourceImage.sizes;
          thumbnail.alt = sourceImage.alt || '';
          thumbnail.loading = 'lazy';
          thumbnail.decoding = 'async';
          mediaCol.appendChild(thumbnail);
        }

        const infoCol = document.createElement('div');
        infoCol.className = 'sf-quickadd-summary__info';
        if (title) infoCol.appendChild(title);
        if (price) infoCol.appendChild(price);

        summary.append(mediaCol, infoCol);
        infoContainer.prepend(summary);
        mediaWrapper.style.display = 'none';
        product.classList.add('sf-quickadd-reflowed');
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

